import { useMemo, useRef, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { useAuth } from "../../../providers/AuthProvider";
import { BrandFormModal } from "./components/BrandFormModal";
import { useBrandsManagement } from "./hooks/useBrandsManagement";
import { downloadCsv } from "../utils/inventoryBulkCsv";
import { getCsvColumnIndex, parseCsvWithHeader } from "../../../lib/csv";
import { api } from "../../../lib/api";

export function BrandsPage() {
  const { user, token } = useAuth();
  const isBusinessOwner = user?.role === "business_owner";
  const isReadOnly = !isBusinessOwner;

  const { brands, loading, error, createBrand, updateBrand, deleteBrand, reload } = useBrandsManagement();

  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const importRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return brands;
    return brands.filter((b) => b.name.toLowerCase().includes(qq));
  }, [brands, q]);

  const selectedCount = selectedIds.size;
  const allFilteredSelected = useMemo(() => filtered.length > 0 && filtered.every((b) => selectedIds.has(String(b.id))), [filtered, selectedIds]);
  const someFilteredSelected = useMemo(() => filtered.some((b) => selectedIds.has(String(b.id))), [filtered, selectedIds]);

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  };

  const toggleSelectAllFiltered = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const b of filtered) {
        if (checked) next.add(String(b.id));
        else next.delete(String(b.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const exportAllBrands = () => {
    if (brands.length === 0) return;
    const rows = brands.map((b) => [b.name, String(b.product_count ?? 0)]);
    const header = ["name", "product_count"];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const csv = [header.join(","), ...rows.map((r) => r.map((x) => (x.includes(",") ? `"${x.replace(/\"/g, "\"\"")}"` : x)).join(","))].join("\r\n");
    downloadCsv(`brands-export-all-${stamp}.csv`, csv);
  };

  const exportSelected = () => {
    const rows = brands
      .filter((b) => selectedIds.has(String(b.id)))
      .map((b) => [b.name, String(b.product_count ?? 0)]);
    const header = ["name", "product_count"];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const csv = [header.join(","), ...rows.map((r) => r.map((x) => (x.includes(",") ? `"${x.replace(/\"/g, "\"\"")}"` : x)).join(","))].join("\r\n");
    downloadCsv(`brands-export-selected-${stamp}.csv`, csv);
  };

  const bulkDeleteSelected = async () => {
    if (!token || isReadOnly || selectedIds.size === 0) return;
    const ok = window.confirm("Soft delete selected brands? Products will be moved to Unknown Brand.");
    if (!ok) return;
    setBulkWorking(true);
    try {
      await api.brands.bulkDelete({ token, ids: Array.from(selectedIds) });
      clearSelection();
      await reload();
    } finally {
      setBulkWorking(false);
    }
  };

  const importCsv = async (file: File) => {
    if (!token || isReadOnly) return;
    const text = await file.text();
    const parsed = parseCsvWithHeader(text);
    const nameIdx = getCsvColumnIndex(parsed.header, "name");
    if (nameIdx < 0) throw new Error("CSV must have a 'name' column");
    const existing = new Set(brands.map((b) => b.name.trim().toLowerCase()));
    for (const r of parsed.rows) {
      const name = (r[nameIdx] ?? "").trim();
      if (!name) continue;
      if (existing.has(name.toLowerCase())) continue;
      await createBrand({ name });
    }
  };

  const openCreate = () => {
    if (isReadOnly) return;
    setMode("create");
    setEditing(null);
    setMutationError(null);
    setModalOpen(true);
  };

  const openEdit = (b: { id: string; name: string }) => {
    if (isReadOnly) return;
    setMode("edit");
    setEditing(b);
    setMutationError(null);
    setModalOpen(true);
  };

  const onSubmit = async (values: { name: string }) => {
    setMutationError(null);
    try {
      if (mode === "create") {
        await createBrand({ name: values.name });
      } else if (mode === "edit" && editing) {
        await updateBrand(editing.id, { name: values.name });
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as Error & { data?: { errors?: Record<string, string[]> } };
      if (err.data?.errors?.name?.length) setMutationError(err.data.errors.name[0]);
      else setMutationError(err.message || "Failed to save brand.");
    }
  };

  const onDelete = async (b: { id: string; name: string }) => {
    if (isReadOnly) return;
    const confirmed = window.confirm(`Delete brand "${b.name}"?`);
    if (!confirmed) return;
    try {
      await deleteBrand(b.id);
    } catch (e) {
      const err = e as Error;
      window.alert(err.message || "Failed to delete brand.");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Brands</h1>
          <p className="text-muted-foreground">Manage product brands.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={exportAllBrands} disabled={loading || brands.length === 0}>
            Export all
          </Button>
          <Button type="button" onClick={openCreate} disabled={isReadOnly}>
            Add Brand
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All brands</CardTitle>
          <CardDescription>Search and manage brands</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <Input placeholder="Search brands..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" />
            <Button type="button" variant="outline" onClick={() => setQ("")}>
              Reset
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={isReadOnly} onClick={() => importRef.current?.click()}>
                Import CSV
              </Button>
              <input
                ref={importRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  void importCsv(f).catch((err) => setMutationError(err instanceof Error ? err.message : "Failed to import CSV"));
                  e.currentTarget.value = "";
                }}
              />
              <Button type="button" variant="outline" onClick={exportSelected} disabled={selectedCount === 0}>
                Export selected
              </Button>
            </div>

            {selectedCount > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{selectedCount} selected</span>
                <Button type="button" size="sm" variant="outline" onClick={clearSelection} disabled={bulkWorking}>
                  Clear
                </Button>
                <Button type="button" size="sm" variant="destructive" onClick={() => void bulkDeleteSelected()} disabled={bulkWorking || isReadOnly}>
                  Delete selected
                </Button>
              </div>
            ) : null}
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed to load brands</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAllFiltered(v === true)}
                    aria-label="Select all filtered brands"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Products</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">No brands found.</TableCell>
                </TableRow>
              ) : (
                filtered.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Checkbox checked={selectedIds.has(String(b.id))} onCheckedChange={(v) => toggleRow(String(b.id), v === true)} />
                    </TableCell>
                    <TableCell className="font-medium">{b.name}</TableCell>
                    <TableCell className="text-right">{b.product_count ?? 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => openEdit(b)} disabled={isReadOnly}>
                          Edit
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(b)} disabled={isReadOnly}>
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <BrandFormModal
        open={modalOpen}
        mode={mode}
        initialName={editing?.name ?? ""}
        onOpenChange={setModalOpen}
        onSubmit={onSubmit}
        error={mutationError}
      />
    </div>
  );
}

